import { useState } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useClientTranslation } from '@/lib/core/i18n';
import { formatAgeRange } from '@/services/platform/data/booking/mews';
import type { StepperRowProps } from '@/services/platform/data/booking/mews/types';
import type { MewsHowManySheetProps } from '../types';
import { SUNBED_LABELS } from '../../labels';
import { cn } from '@/lib/core/utils/cn';
import { Icon } from '@/components/ui/icon';

function StepperRow({
  label,
  description,
  value,
  min,
  max,
  onDecrement,
  onIncrement,
  borderedValue,
}: StepperRowProps & { borderedValue?: boolean }) {
  const t = useClientTranslation();

  return (
    <div className="border-jncquoi-neutral-200 flex items-center gap-4 border-b-[0.5px] py-6">
      <div className="flex flex-1 flex-col gap-1">
        <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
          {label}
        </p>
        <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] font-normal tracking-[0.5px]">
          {description}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onDecrement}
          disabled={value <= min}
          className="text-jncquoi-neutral-1000 flex size-11 cursor-pointer items-center justify-center rounded-full text-[20px] font-light disabled:opacity-30"
          aria-label={t('App.Labels.Decrease')}
        >
          <Icon name="minus" width={24} height={24} strokeWidth={1.5} className="text-jncquoi-neutral-1000" />
        </button>
        <div
          className={cn(
            'flex w-15 items-center justify-center p-2',
            borderedValue && 'border-jncquoi-neutral-200 border'
          )}
        >
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-center text-[14px] font-normal tracking-[0.5px]">
            {String(value).padStart(2, '0')}
          </span>
        </div>
        <button
          type="button"
          onClick={onIncrement}
          disabled={max !== undefined && value >= max}
          className="text-jncquoi-neutral-1000 flex size-11 cursor-pointer items-center justify-center rounded-full text-[20px] font-light disabled:opacity-30"
          aria-label={t('App.Labels.Increase')}
        >
          <Icon name="plus" width={24} height={24} strokeWidth={1.5} className="text-jncquoi-neutral-1000" />
        </button>
      </div>
    </div>
  );
}

export function MewsHowManySheet({
  isOpen,
  onClose,
  cmsProps,
  ageConfig,
  guestsMax,
  numAdults: initialAdults,
  numChildren: initialChildren,
  onSelect,
  labels = SUNBED_LABELS,
  borderedValue,
}: MewsHowManySheetProps) {
  const t = useClientTranslation();
  const [adults, setAdults] = useState(initialAdults);
  const [children, setChildren] = useState(initialChildren);

  const title = cmsProps?.title ?? t('App.Labels.HowMany');
  const subtitle = cmsProps?.subtitle ?? '';
  const sectionLabel = cmsProps?.formTitle ?? t(labels.guestsSectionLabel);
  const selectLabel = cmsProps?.selectButtonLabel ?? t('App.Labels.Select');

  // Derive age descriptions from API config; fall back to i18n strings.
  const adultCategory = ageConfig?.ageCategories?.find(c => c.classification === 'Adult');
  const childCategory = ageConfig?.ageCategories?.find(c => c.classification === 'Child');
  const adultsAgesLabel =
    formatAgeRange(adultCategory?.minimalAge, adultCategory?.maximalAge) ?? t(labels.adultsAges);
  const childrenAgesLabel =
    formatAgeRange(childCategory?.minimalAge, childCategory?.maximalAge) ?? t(labels.childrenAges);
  // Hide the children stepper when the API explicitly disables child selection.
  // Defaults to showing when ageConfig is not yet loaded (undefined).
  const showChildren = ageConfig?.childSelectionEnabled !== false;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="border-jncquoi-neutral-200 border-t-[0.5px]">
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-2 px-4 pt-8">
          <div className="flex items-center gap-8">
            <h2 className="font-pp-neue-montreal flex-1 text-[32px] font-medium tracking-[0.5px] text-[#2a2a2a]">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-jncquoi-neutral-1000 flex size-11 cursor-pointer items-center justify-center rounded-full"
              aria-label={t('App.Labels.Close')}
            >
              <Icon
                name="xmark"
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-jncquoi-neutral-1000"
              />
            </button>
          </div>
          {subtitle && (
            <p className="font-pp-neue-montreal text-[16px] tracking-[0.25px] text-[#707070]">{subtitle}</p>
          )}
        </div>

        {/* Scrollable content */}
        <div data-base-ui-swipe-ignore className="flex-1 overflow-y-auto">
          <div className="px-4 pt-8">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[14px] font-normal tracking-[0.5px] uppercase">
              {sectionLabel}
            </p>
            <div className="flex flex-col">
              <StepperRow
                borderedValue={borderedValue}
                label={t(labels.adults)}
                description={adultsAgesLabel}
                value={adults}
                min={1}
                max={guestsMax != null ? guestsMax - children : undefined}
                onDecrement={() => setAdults(v => Math.max(1, v - 1))}
                onIncrement={() => setAdults(v => v + 1)}
              />
              {showChildren && (
                <StepperRow
                  borderedValue={borderedValue}
                  label={t(labels.children)}
                  description={childrenAgesLabel}
                  value={children}
                  min={0}
                  max={guestsMax != null ? guestsMax - adults : undefined}
                  onDecrement={() => setChildren(v => Math.max(0, v - 1))}
                  onIncrement={() => setChildren(v => v + 1)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-jncquoi-neutral-400 pb-safe-area shrink-0 border-t-[0.5px] px-4 pt-6">
          <button
            type="button"
            onClick={() => {
              onSelect(adults, children);
              onClose();
            }}
            className="font-pp-neue-montreal bg-jncquoi-neutral-1000 flex h-13 w-full cursor-pointer items-center justify-center rounded-[2px] text-[12px] font-semibold tracking-[0.5px] text-white uppercase"
          >
            {selectLabel}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
